import {
  createContext,
  lazy,
  Suspense,
  useContext,
  useEffect,
  type ComponentType,
} from 'react'
import { Screen, SterlingPage } from 'ui'
import Loading from '../components/utils/Loading'
import { loadOptionalChunk } from './staleChunkReload'

const pageLoading = (
  <div className="flex min-h-[50vh] flex-1 flex-col justify-center animate-in fade-in-0 fill-mode-both [--tw-animation-delay:200ms]">
    <Loading />
  </div>
)

const FALLBACKS = {
  screen: <Loading additionalClasses={{ 'h-screen': true }} />,
  page: pageLoading,
  overlay: null,
}

const framedPageLoading = (
  <Screen>
    <SterlingPage>{pageLoading}</SterlingPage>
  </Screen>
)

const PageFrameContext = createContext(false)

/**
 * Marks its subtree as the sidebar shell's page area, where every page draws
 * its own `Screen` + `SterlingPage` frame. A `'page'` fallback rendered there
 * draws the same frame, so a first visit does not flash an unframed page.
 * A lazy page resets it for its own subtree: nested routes load inside the
 * frame the parent page already drew.
 */
export const PageFrameProvider = PageFrameContext.Provider

type Preloadable = { preload: () => void }

type LazyComponentOptions = {
  /** Chunks to fetch once this component has loaded and mounted. */
  preloadOnMount?: () => Preloadable[]
}

export function lazyComponent<Module, Name extends keyof Module>(
  load: () => Promise<Module | undefined>,
  name: Name,
  fallback: keyof typeof FALLBACKS = 'page',
  { preloadOnMount }: LazyComponentOptions = {},
) {
  type Props = Module[Name] extends ComponentType<infer P> ? P : never
  const Lazy = lazy(async () => {
    // Vite resolves import() to undefined once a vite:preloadError listener
    // calls preventDefault (lib/staleChunkReload.ts): the page is reloading, so
    // hold the fallback rather than render a module that never arrived.
    const module = await load()
    if (!module) return new Promise<never>(() => {})
    return { default: module[name] as ComponentType<object> }
  })

  function Loaded(props: object) {
    useEffect(() => {
      preloadOnMount?.().forEach((component) => component.preload())
    }, [])
    return <Lazy {...props} />
  }

  function LazyComponent(props: Props) {
    const inPageFrame = useContext(PageFrameContext)
    return (
      <Suspense
        fallback={
          inPageFrame && fallback === 'page'
            ? framedPageLoading
            : FALLBACKS[fallback]
        }
      >
        <PageFrameProvider value={false}>
          <Loaded {...(props as object)} />
        </PageFrameProvider>
      </Suspense>
    )
  }

  /**
   * Fetches the chunk ahead of the first render. A failure is dropped rather
   * than reloading the page: the render loads it again and handles its own.
   */
  LazyComponent.preload = () => {
    loadOptionalChunk(load).catch(() => {})
  }

  return LazyComponent
}
